// 链接localStorage中存储的polygon数据的加载器

import turfBuffer from '@turf/buffer';
import { Feature, FeatureCollection, Point, Polygon as GEOPolygon } from 'geojson';
import Schema from 'schema.io';
import {
  clearVirtualGroupLink,
  create,
  findInBoundary,
  removeById,
  updateById,
  updateEnableOverageLimit,
  updateHasBluetoothSpike,
  updateOverageLimitCount, updateRatedParkingCapacity, updateStockBelowAlarmCount, updateStockBacklogAlarmCount, updateParkingNameAndSave, updateParkingSortAndSave
} from '../../api/operation/geojson';
import { LS_POLYGON_STORES_KEY } from '../../utils/constants';
import SingletonEventEmitterClass from '../SingletonEventEmitterClass';
import Polygon from './Polygon';

const polygonStoresSchema = new Schema([{
  points: [[Number]],
  remote: {
    type: String,
    required: false,
  },
}]);

interface IPolygonStoreInLocalStorage {
  points: ILngLat[];
  remote?: string;
}

interface IPolygonStore {
  polygon: Polygon;
  remote?: string;
}

interface IRemotePolygon {
  _id: string;
  hash: string;
  gcj02Geometry: {
    type: 'Polygon';
    coordinates: number[][][];
    properties?: IObject;
  };
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
    properties?: IObject;
  };
  region:{
    _id:string;
    name:string;
  }
}

export default class PolygonsLoader extends SingletonEventEmitterClass {
  private _editingPolygon?: Polygon;
  private _polygonStores?: IPolygonStore[];
  private _remotePolygons?: IRemotePolygon[];
  private _remotePolygonsNoRegion?: IRemotePolygon[];
  private _boundary?: [ILngLat, ILngLat];
  private _buffer: number = 0.003;

  public get geojsonPolygons(): FeatureCollection<GEOPolygon> {
    return {
      type: 'FeatureCollection',
      features: this._polygonStores!.map(store => {
        return store.polygon.geojsonPolygon;
      }),
    };
  }

  public get geojsonEditor(): FeatureCollection<Point> {
    if (!this._editingPolygon) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }
    return this._editingPolygon.geojsonEditor;
  }

  public get geojsonRemotePolygons(): FeatureCollection<GEOPolygon> {
    return {
      type: 'FeatureCollection',
      features: this._remotePolygons!.map<Feature<GEOPolygon>>((polygon: any) => {
        return {
          type: 'Feature',
          geometry: polygon.geometry,
          properties: {
            id: polygon._id,
            info: {
              ...polygon.info, layerType: 'parkLot',
            },
          },
        };
      }),
    };
  }

  public get geojsonRemoteNoRegionPolygons(): FeatureCollection<GEOPolygon> {
    return {
      type: 'FeatureCollection',
      features: this._remotePolygonsNoRegion!.map<Feature<GEOPolygon>>((polygon: any) => {
        return {
          type: 'Feature',
          geometry: polygon.geometry,
          properties: {
            id: polygon._id,
            info: {
              ...polygon.info, layerType: 'parkLot',
            },
          },
        };
      }),
    };
  }

  public get geojsonRemotePolygonsBuffer(): FeatureCollection<GEOPolygon> {
    return turfBuffer(this.geojsonRemotePolygons, this._buffer) as FeatureCollection<GEOPolygon>;
  }
  public get geojsonRemotePolygonsNoRegionBuffer(): FeatureCollection<GEOPolygon> {
    return turfBuffer(this.geojsonRemoteNoRegionPolygons, this._buffer) as FeatureCollection<GEOPolygon>;
  }

  public get editingPolygon() {
    return this._editingPolygon;
  }

  public get polygonStoresCount() {
    return this._polygonStores!.length;
  }

  private get _updatedRemotePolygonIds(): string[] {
    return this._polygonStores!.filter(store => store.remote).map(store => store.remote!);
  }

  constructor() {
    super();
    if (PolygonsLoader.__new) {
      this._polygonStores = this._readFromLocalStorage();
      this._remotePolygons = [];
      this._remotePolygonsNoRegion = [];
      document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.code === 'Backspace') {
          if (this._editingPolygon) {
            const polygonStores = this._polygonStores!;
            const polygonStore = polygonStores.find(store => store.polygon === this._editingPolygon);
            if (polygonStore) {
              polygonStores.splice(polygonStores.indexOf(polygonStore), 1);
              if (polygonStore.remote) {
                removeById(polygonStore.remote).catch(error => {
                  console.error(error);
                  this.refresh();
                });
              }
            }
            this._editingPolygon = undefined;
            this._triggerPolygonsUpdated();
          }
        }
      });
    }
  }

  public adjustBuffer(buffer: number) {
    this._buffer = buffer;
    this._triggerPolygonsBufferUpdated();
  }

  public cancelEditing() {
    this._editingPolygon = undefined;
    this._triggerEditorUpdated();
  }

  public addPolygon(points: ILngLat[], remote?: string) {
    const polygon = this._newPolygon(points);
    const polygonStores = this._polygonStores!;
    const samePolygonStore = polygonStores.find(store => {
      return store.polygon.hash === polygon.hash;
    });
    if (samePolygonStore) {
      return;
    }
    this._polygonStores!.push({
      polygon,
      remote,
    });
    this._editingPolygon = polygon;
    this._triggerPolygonsUpdated();
  }

  public toggleSelectPolygon(id: string) {
    const store = this._polygonStores!.find(polygonStore => {
      return polygonStore.polygon.id === id;
    });
    if (!store) {
      return;
    }
    this._editingPolygon = this._editingPolygon === store.polygon ? undefined : store.polygon;
    this._triggerEditorUpdated();
  }

  public onMapUpdatedBoundary(boundary: [ILngLat, ILngLat]) {
    this._boundary = boundary;
    this._findPolygonsInBoundary(boundary);
  }

  public onMapClickedRemotePolygon(id: string) {
    const remotePolygons = this._remotePolygons!;
    const remotePolygonsNoRegion = this._remotePolygonsNoRegion!;
    const remotePolygon = remotePolygons.find(polygon => polygon._id === id) as IRemotePolygon;
    const remotePolygonNoRegion = remotePolygonsNoRegion.find(polygon => polygon._id === id) as IRemotePolygon;
    if (remotePolygon) {
      const points = [...remotePolygon.geometry.coordinates[0]];
      points.pop();
      this.addPolygon(points as ILngLat[], id);
      remotePolygons.splice(remotePolygons.indexOf(remotePolygon), 1);
      this._triggerPolygonsUpdated();
      this._triggerRemotePolygonsUpdated();
    }
    if (remotePolygonNoRegion) {
      const points = [...remotePolygonNoRegion.geometry.coordinates[0]];
      points.pop();
      this.addPolygon(points as ILngLat[], id);
      remotePolygonsNoRegion.splice(remotePolygonsNoRegion.indexOf(remotePolygonNoRegion), 1);
      this._triggerPolygonsUpdated();
      this._triggerRemotePolygonsUpdated();
    }
  }

  public refresh() {
    if (this._boundary) {
      this._findPolygonsInBoundary(this._boundary);
    }
  }

  public async publish() {
    if (this.polygonStoresCount === 0) {
      return;
    }
    this._editingPolygon = undefined;
    const stores = this._polygonStores!;
    while (stores.length > 0) {
      const store = stores[0];
      if (store.remote) {
        await updateById(store.remote, store.polygon.geometry.coordinates as ILngLat[][]);
      } else {
        await create(store.polygon.geometry.coordinates as ILngLat[][]);
      }
      stores.splice(0, 1);
      this._triggerPolygonsUpdated();
    }
    this.refresh();
  }

  public async reset() {
    this._polygonStores = [];
    this._editingPolygon = undefined;
    this._triggerPolygonsUpdated();
    this.refresh();
  }

  public async updateRatedParkingCapacity(parkingLotId: string, ratedParkingCapacity: number){
    await updateRatedParkingCapacity(parkingLotId, ratedParkingCapacity);
    this._triggerPolygonsUpdated();
  }

  public async updateHasBluetoothSpike(parkingLotId: string, hasBluetoothSpike: boolean){
    await updateHasBluetoothSpike(parkingLotId, hasBluetoothSpike);
    this._triggerPolygonsUpdated();
  }

  public async updateEnableOverageLimit(parkingLotId: string, enableOverageLimit: boolean){
    await updateEnableOverageLimit(parkingLotId, enableOverageLimit);
    this._triggerPolygonsUpdated();
  }

  public async updateOverageLimitCount(parkingLotId: string, overageLimitCount: number){
    await updateOverageLimitCount(parkingLotId, overageLimitCount);
    this._triggerPolygonsUpdated();
  }

  public async updateStockBelowAlarmCount(parkingLotId: string, count: number){
    await updateStockBelowAlarmCount(parkingLotId, count);
    this._triggerPolygonsUpdated();
  }

  public async updateStockBacklogAlarmCount(parkingLotId: string, count: number){
    await updateStockBacklogAlarmCount(parkingLotId, count);
    this._triggerPolygonsUpdated();
  }

  public async updateParkingNameAndSave(parkingLotId: string, name: string){
    await updateParkingNameAndSave(parkingLotId, name);
    this._triggerPolygonsUpdated();
  }

  public async updateParkingSortAndSave(parkingLotId: string, sort: number, name: string){
    await updateParkingSortAndSave(parkingLotId, sort, name);
    this._triggerPolygonsUpdated();
  }

  public async clearVirtualGroupLink(parkingLotId: string){
    await clearVirtualGroupLink(parkingLotId);
    this._triggerPolygonsUpdated();
  }

  private _saveToLocalStorage() {
    localStorage.setItem(LS_POLYGON_STORES_KEY, JSON.stringify(this._polygonStores!.map((polygonStore: IPolygonStore) => {
      return {
        points: polygonStore.polygon.points,
        remote: polygonStore.remote,
      } as IPolygonStoreInLocalStorage;
    })));
  }

  private _triggerPolygonsBufferUpdated() {
    this.emit('polygons_buffer_updated');
  }

  private _triggerPolygonsUpdated() {
    this.emit('polygons_updated');
    this._triggerEditorUpdated();
    this._saveToLocalStorage();
  }

  private _triggerEditorUpdated() {
    this.emit('polygon_editor_updated');
  }

  private _triggerRemotePolygonsUpdated() {
    this.emit('remote_polygons_updated');
  }

  private _newPolygon(points: ILngLat[]) {
    const polygon = new Polygon(points);
    polygon.on('updated', () => {
      this._triggerPolygonsUpdated();
    });
    return polygon;
  }

  private _readFromLocalStorage() {
    const data = localStorage.getItem(LS_POLYGON_STORES_KEY);
    if (data) {
      try {
        const polygonStores = JSON.parse(data) as IPolygonStoreInLocalStorage[];
        polygonStoresSchema.validate(polygonStores);
        return polygonStores.map(polygonStore => {
          return {
            polygon: this._newPolygon(polygonStore.points),
            remote: polygonStore.remote,
          } as IPolygonStore;
        });
      } catch (e) {
        return [];
      }
    } else {
      return [];
    }
  }

  private _findPolygonsInBoundary(boundary: [ILngLat, ILngLat]) {
    (async () => {
      const { data } = await findInBoundary<GEOPolygon>(boundary);
      const updatedRemotePolygonIds = this._updatedRemotePolygonIds;
      const polygons: IRemotePolygon[] = data.map((item: any) => {
        return { ...item.polygon, _id: item._id, region: item.region, info: item };
      });
      this._remotePolygons = polygons.filter(item => item && (!updatedRemotePolygonIds.includes(item._id)&&((item.region || {}).name)));
      this._remotePolygonsNoRegion = polygons.filter(item => item && (!updatedRemotePolygonIds.includes(item._id)&&(!(item.region || {}).name)));
      this._triggerRemotePolygonsUpdated();
      this._triggerPolygonsBufferUpdated();
    })().catch(console.error);
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1-0!./src/classes/loaders/PolygonsLoader.ts